import {
  PolicyType,
  IInvalidField,
  IPolicy,
  policyConfigV2,
  IPolicyResponseItem,
  SubscriptionStatus,
  AuditLogEntryType,
} from 'shared';
import {
  Case,
  Switch,
  When,
} from 'react-if';
import {
  AdjustmentsVerticalIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { BeakerIcon } from '@heroicons/react/20/solid';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

import { Button } from '../../../../common/Atoms/Button';
import { Modal } from '../../../../common/Atoms/Modal';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { Notification } from '../../../../common/Atoms/Notification';
import { Form } from '../../Molecules/Form';
import { usePolicySanityChecks } from '../../../hooks/usePolicySanityChecks';
import { LoadingOverlay } from '../../Molecules/LoadingOverlay';
import { ITab, Tabs } from '../../../../common/Atoms/Tabs';
import { useAdvancedPolicyFields } from '../../../hooks/useAdvancedPolicyFields';
import { classNames } from '../../../../common/lib/classNames';
import { useGetSubscriptionMetadata } from '../../../hooks/useGetSubscription';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';

import { PolicyLateFee } from './PolicyTypes/PolicyLateFee';
import { PolicyPromptPaymentDiscount } from './PolicyTypes/PolicyPromptPaymentDiscount';
import { PolicyReminder } from './PolicyTypes/PolicyReminder';
import { PolicySms } from './PolicyTypes/PolicySms';
import { GroupSelector } from './PolicyForm/GroupSelector';
import { AdvancedFields } from './PolicyForm/AdvancedFields/AdvancedFields';
import { PolicyIntervalLateFee } from './PolicyTypes/PolicyStatementLateFee';
import { PolicyStatement } from './PolicyTypes/PolicyStatement';
import { PolicyEscalation } from './PolicyTypes/PolicyEscalation';
import { PolicyQuote } from './PolicyTypes/PolicyQuote';
import { ITitleSuggestion, PolicyTitle } from './PolicyForm/Fields/PolicyTitle';
import { PolicyContactEscalation } from './PolicyTypes/PolicyContactEscalation';
import { PolicyPaymentPlanReminder } from './PolicyTypes/PolicyPaymentPlanReminder';
import { CopyPolicyModal } from './CopyPolicyModal';

// For the children components
export interface IPolicyProps {
  policy: IPolicy;
  invalids: IInvalidField[];
  handleChange: (obj: { name: string; value: string | number | boolean | Date }) => void;
  hasCompletedActions?: boolean;
}

export type HandleChange = (obj: { name: string; value: string | number | boolean | Date | number[] }, isGroupsChange?: boolean) => void;

interface IPolicyItemProps {
  onSave: (closeAfterSave?: boolean) => void;
  onDelete: () => void;
  /** Shows the "Try the AI assistant" button; only the policy page passes this (the drawer needs the route's policyId) */
  onTryAssistant?: () => void;
  invalids: IInvalidField[];
  loading?: boolean;
  handleChange: HandleChange;
  policyFormData: IPolicyResponseItem | null;
  formDataDirty: boolean;
  hasCompletedActions: boolean;
  titleSuggestion?: ITitleSuggestion;
}

export function PolicyItem({
  onSave,
  onDelete,
  onTryAssistant,
  invalids,
  loading,
  handleChange,
  policyFormData,
  formDataDirty,
  hasCompletedActions,
  titleSuggestion,
}: IPolicyItemProps) {
  // Hooks
  const [tab, setTab] = useState<`main` | `advanced` | `test`>(`main`);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const advancedFieldsInUse = useAdvancedPolicyFields(policyFormData?.policy);
  const { subscriptionMetadata, isBillingOwner } = useGetSubscriptionMetadata();
  const currentOrg = useGetSelectedOrganisation();

  function renderSmsLimitWarning() {
    if (!subscriptionMetadata || !subscriptionMetadata.id) {
      return null;
    }

    if (subscriptionMetadata.status !== SubscriptionStatus.ACTIVE) {
      return null;
    }

    if (!policyFormData || !policyFormData.policy || policyFormData.policy.policy_type !== PolicyType.SMS) {
      return null;
    }

    if (!subscriptionMetadata.smsBillingEnabled) {
      return (
        <Notification type={ `error` }>
          {
            `SMS Billing is currently disabled for the linked subscription.`
          }
          <When condition={ isBillingOwner }>
            { ` You can enable SMS billing in your ` }
            <Link to={ `/billing/subscriptions/${subscriptionMetadata.id}` }
              className={ `underline` }>
              { `subscription` }
            </Link>
            { `.` }
          </When>

          <When condition={ !isBillingOwner }>
            { ` Ask the subscription owner to enable SMS billing.` }
          </When>
        </Notification>
      );
    }

    const type = subscriptionMetadata.smsLimit < 30 ? `warning` : `info`;

    return (
      <Notification type={ type }>
        {
          `Your current SMS limit per month is `
        }
        <strong>
          { subscriptionMetadata.smsLimit }
        </strong>
        { ` messages.` }
        <When condition={ isBillingOwner }>
          { ` You can increase this limit in your ` }
          <Link to={ `/billing/subscriptions/${subscriptionMetadata.id}` }
            className={ `underline` }>
            { `subscription` }
          </Link>
          { `.` }
        </When>

        <When condition={ !isBillingOwner }>
          { ` Ask the subscription owner to increase this limit.` }
        </When>
      </Notification>
    );
  }

  const tabs: ITab[] = useMemo(() => {
    const arr: ITab[] = [
      {
        name: `Policy Configuration`,
        onClick: () => setTab(`main`),
        href: `main`,
        icon: Cog6ToothIcon,
        current: tab === `main`,
      },
      {
        name: `More Options & Filters`,
        onClick: () => setTab(`advanced`),
        href: `advanced`,
        icon: AdjustmentsVerticalIcon,
        current: tab === `advanced`,
        badge: advancedFieldsInUse.length ? {
          text: `${advancedFieldsInUse.length}`,
          color: `gray`,
        } : undefined,
      },
    ];

    // arr.push({
    //   name: `Test & Preview`,
    //   onClick: () => setTab(`test`),
    //   href: `test`,
    //   icon: RocketLaunchIcon,
    //   current: tab === `test`,
    // });

    return arr;
  }, [tab, advancedFieldsInUse, policyFormData]);

  const warnings = usePolicySanityChecks(policyFormData?.policy);

  if (!policyFormData || !policyFormData.policy) {
    return null;
  }

  const { policy } = policyFormData;

  if (policy.policy_type && policy?.system && !policyConfigV2[policy.policy_type]?.allowedSystemEditor) {
    toast.error(`Not allowed to edit this policy type. Please contact support.`);
    console.error(`Not allowed to edit this policy type`);

    return null;
  }

  return (
    <LoadingOverlay
      loading={ loading }>
      <Form>
        <div
          className={ `flex flex-col justify-between h-full` }
        >
          <GroupSelector
            policyFormData={ policyFormData }
            handleChange={ handleChange }
          />

          <div className={ classNames(
            `space-y-6`,
          ) }>
            <div className={ `flex items-end` }>
              <Tabs
                navigational={ false }
                tabs={ tabs }
                className={ `pb-0 flex-1 min-w-0` }
              />
              <div className={ `border-b border-gray-200 pb-2 pl-2` }>
                <button
                  type={ `button` }
                  onClick={ () => setShowDeleteModal(true) }
                  title={ `Delete policy` }
                  aria-label={ `Delete policy` }
                  className={ `text-gray-400 hover:text-red-700 cursor-pointer` }
                >
                  <TrashIcon className={ `w-6 h-6` } />
                </button>
              </div>
            </div>

            <When condition={ tab === `main` }>
              <PolicyTitle
                policy={ policy }
                invalids={ invalids }
                handleChange={ handleChange }
                suggestion={ titleSuggestion }
              />
              <Switch>
                <Case condition={ policy.policy_type === PolicyType.LATE_FEE }>
                  <PolicyLateFee
                    policy={ policyFormData.policy }
                    handleChange={ handleChange }
                    invalids={ invalids }
                  />
                </Case>
                <Case condition={ policy.policy_type === PolicyType.STATEMENT_LATE_FEE }>
                  <PolicyIntervalLateFee
                    policy={ policyFormData.policy }
                    handleChange={ handleChange }
                    invalids={ invalids }
                  />
                </Case>
                <Case condition={ policy.policy_type === PolicyType.DISCOUNT }>
                  <PolicyPromptPaymentDiscount
                    policy={ policyFormData.policy }
                    handleChange={ handleChange }
                    invalids={ invalids }
                  />
                </Case>
                <Case condition={ policy.policy_type === PolicyType.REMINDER }>
                  <PolicyReminder
                    policy={ policyFormData.policy }
                    handleChange={ handleChange }
                    invalids={ invalids }
                  />
                </Case>
                <Case condition={ policy.policy_type === PolicyType.SMS }>
                  <PolicySms
                    policy={ policyFormData.policy }
                    handleChange={ handleChange }
                    invalids={ invalids }
                  />
                </Case>
                <Case condition={ policy.policy_type === PolicyType.STATEMENT }>
                  <PolicyStatement
                    policy={ policyFormData.policy }
                    handleChange={ handleChange }
                    invalids={ invalids }
                  />
                </Case>
                <Case condition={ policy.policy_type === PolicyType.ESCALATION }>
                  <PolicyEscalation
                    policy={ policyFormData.policy }
                    handleChange={ handleChange }
                    invalids={ invalids }
                    hasCompletedActions={ hasCompletedActions }
                  />
                </Case>
                <Case condition={ policy.policy_type === PolicyType.CONTACT_ESCALATION }>
                  <PolicyContactEscalation
                    policy={ policyFormData.policy }
                    handleChange={ handleChange }
                    invalids={ invalids }
                    hasCompletedActions={ hasCompletedActions }
                  />
                </Case>
                <Case condition={ policy.policy_type === PolicyType.QUOTE_EMAIL }>
                  <PolicyQuote
                    policy={ policyFormData.policy }
                    handleChange={ handleChange }
                    invalids={ invalids }
                  />
                </Case>
                <Case condition={ policy.policy_type === PolicyType.PAYMENT_PLAN_REMINDER }>
                  <PolicyPaymentPlanReminder
                    policy={ policyFormData.policy }
                    handleChange={ handleChange }
                    invalids={ invalids }
                  />
                </Case>
              </Switch>
            </When>
            <When condition={ tab === `advanced` }>
              <div className={ `relative` }>
                { currentOrg && policy.id && (
                  <Link
                    to={ `/${currentOrg.id}/logs?entityType=${AuditLogEntryType.Policy}&entityId=${policy.id}` }
                    className={ `absolute top-4 right-4 text-xs text-gray-400 hover:text-gray-600 hover:underline z-10` }
                  >
                    { `view history` }
                  </Link>
                ) }
                <AdvancedFields
                  policyFormData={ policyFormData.policy }
                  handleChange={ handleChange }
                  invalids={ invalids }
                />
              </div>
            </When>

            { /* <When condition={ tab === `test` }>
              <PolicyTestViewRoot
                policyFormData={ policyFormData }
                unsavedChanges={ formDataDirty }
                onSave={ onSave }
              />
            </When> */ }
          </div>

          <When condition={ warnings.length }>
            <div className={ `space-y-2` }>
              {
                warnings.map(warning => (
                  <Notification type={ `warning` }
                    key={ warning }>
                    { warning }
                  </Notification>
                ))
              }
            </div>
          </When>
          <Switch>
            <Case condition={ invalids.length }>
              <div className={ `transition ease-in-out duration-250 mb-4` }>
                <Notification
                  type={ `error` }
                  title={ `Errors found in policy:` }
                >
                  <span className={ `flex flex-col` }>
                    {
                      invalids.map(err => (
                        <span key={ err.message }>
                          { err.message }
                        </span>
                      ))
                    }
                  </span>
                </Notification>
              </div>
            </Case>
          </Switch>

          <When condition={ policy.policy_type === PolicyType.SMS }>
            <div className={ `mt-4 mb-4 space-y-2` }>
              <When condition={ subscriptionMetadata?.smsBillingEnabled }>
                <Notification
                  type={ `info` }
                >
                  { `SMS messages are charged per message at your subscription currency rate` }
                </Notification>
              </When>
              { renderSmsLimitWarning() }
            </div>
          </When>

          <When condition={ policy.policy_type === PolicyType.QUOTE_EMAIL || policy.policy_type === PolicyType.PAYMENT_PLAN_REMINDER }>
            <Notification
              type={ `pro` }
              icon={ BeakerIcon }
              className={ `mt-2` }
            >
              { `This is a brand new feature! Please let us know if you have any feedback or requests!` }
            </Notification>
          </When>

          <div className={ `flex justify-between mt-1 sm:mt-6` }>
            <div className={ `flex justify-start space-x-2` }>
              <When condition={ !!onTryAssistant }>
                <Button
                  onClick={ onTryAssistant }
                  color={ `gray` }
                >
                  <SparklesIcon className={ `mr-1 inline h-4 w-4 text-indigo-600` } />
                  { `Try the AI assistant` }
                </Button>
              </When>
              <Button
                onClick={ () => setShowCopyModal(true) }
                color={ `gray` }
                disabled={ formDataDirty }
                tooltip={ formDataDirty ? `Save your changes before copying` : undefined }
              >
                <DocumentDuplicateIcon className={ `mr-1 inline h-4 w-4` } />
                { `Copy Policy` }
              </Button>
            </div>

            <Button
              onClick={ () => onSave() }
              disabled={ !formDataDirty }
            >
              { `Save` }
            </Button>
          </div>
        </div>
      </Form>

      <Modal
        title={ `Delete policy?` }
        open={ showDeleteModal }
        setOpen={ setShowDeleteModal }
      >
        <div className={ `mt-4 space-y-6` }>
          <Paragraph>
            { `"${policy.title}" will be deleted and removed from its groups. This cannot be undone.` }
          </Paragraph>
          <div className={ `flex justify-end space-x-2` }>
            <Button
              color={ `gray` }
              onClick={ () => setShowDeleteModal(false) }
            >
              { `Cancel` }
            </Button>
            <Button
              color={ `red` }
              onClick={ () => {
                setShowDeleteModal(false);
                onDelete();
              } }
            >
              { `Delete Policy` }
            </Button>
          </div>
        </div>
      </Modal>

      <CopyPolicyModal
        open={ showCopyModal }
        onClose={ () => setShowCopyModal(false) }
        policy={ policy }
      />
    </LoadingOverlay>
  );
}
